import { notifications } from '@mantine/notifications';
import { IconArrowsExchange, IconCheck, IconFolders, IconMinus } from '@tabler/icons-react';
import type {
    MoveResourceType,
    ProjectBaremetalSummary,
    ProjectBlockVolumeSummary,
    ProjectContainerSummary,
    ProjectInstanceSummary,
    ProjectModel,
    ProjectResourcesModel,
} from '@xver/apis/v1/projects/model';
import { Button } from '@xver/components/Atoms';
import {
    DataTable,
    DataTableColumn,
    MoveResourceItem,
    MoveResourcesModal,
} from '@xver/components/Organisms';
import { useActiveProjectScope } from '@xver/hooks/useActiveProject';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import { useProjectResourcesQuery } from '@xver/hooks/useProjectQueries';
import AttachmentSummary from '@xver/pages/private/product/BlockStoragePage/components/AttachmentSummary';
import VolumeStatusBadge from '@xver/pages/private/product/BlockStoragePage/components/VolumeStatusBadge';
import { getStatusText } from '@xver/pages/private/product/CloudComputePage/components/InstanceListTable/InstanceListTable.utils';
import { setActiveProject } from '@xver/store/project/project.actions';
import { formatBaht } from '@xver/utils/number';
import {
    focusAfterRender,
    PRODUCT_OF_RESOURCE,
    resourceKey,
    selectionState,
} from '@xver/utils/projects';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DateTime } from '../../ProjectsPage/components/ProjectBits';
import { LoadError } from './MembersTabPanel';

export type ResourceSectionKey = keyof ProjectResourcesModel;

export const resourceSectionHeadingId = (key: ResourceSectionKey) => `project-resources-${key}`;

type Selection = ReadonlyMap<string, MoveResourceItem>;

const SECTION_OF_TYPE: Record<MoveResourceType, ResourceSectionKey> = {
    instance: 'instances',
    baremetal: 'baremetals',
    container: 'containers',
};

/** Makes `project` active before following a resource link (kufer authorizes by the header). */
function useOpenInProject(project: ProjectModel) {
    const { t } = useTranslation('projects');
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId } = useActiveProjectScope();

    return () => {
        if (userId === undefined || project.id === activeProjectId) return;
        dispatch(setActiveProject({ userId, projectId: project.id }));
        notifications.show({
            title: t('aligned.title'),
            message: t('aligned.message', { name: project.name }),
            icon: <IconFolders size={20} aria-hidden="true" />,
        });
    };
}

interface TableCheckboxProps {
    label: string;
    checked: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}

/**
 * A 20 px box with a 40 px invisible hit area (A11Y.md: dense tables expand the hit area): the real
 * native checkbox is transparent and covers the drawn box, so keyboard, screen readers and clicks
 * all go to it.
 */
function TableCheckbox({
    label,
    checked,
    indeterminate = false,
    disabled,
    onChange,
}: TableCheckboxProps) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <span className="relative inline-flex size-5 shrink-0 align-middle">
            <input
                ref={ref}
                type="checkbox"
                aria-label={label}
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.currentTarget.checked)}
                className="peer absolute -inset-2.5 m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
            />
            <span
                aria-hidden="true"
                className="pointer-events-none flex size-5 items-center justify-center rounded-[4px] border border-[var(--color-input-border)] bg-[var(--mantine-color-default)] text-white peer-checked:border-[var(--mantine-primary-color-filled)] peer-checked:bg-[var(--mantine-primary-color-filled)] peer-indeterminate:border-[var(--mantine-primary-color-filled)] peer-indeterminate:bg-[var(--mantine-primary-color-filled)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-solid peer-focus-visible:outline-[var(--mantine-primary-color-filled)] peer-disabled:opacity-50"
            >
                {indeterminate ? (
                    <IconMinus size={14} stroke={3} />
                ) : checked ? (
                    <IconCheck size={14} stroke={3} />
                ) : null}
            </span>
        </span>
    );
}

interface ResourceSectionProps<T> {
    sectionKey: ResourceSectionKey;
    type: MoveResourceType;
    title: string;
    rows: T[];
    columns: DataTableColumn<T>[];
    toItem: (row: T) => MoveResourceItem;
    selectable: boolean;
    selected: Selection;
    onToggle: (items: MoveResourceItem[], select: boolean) => void;
    emptyMessage: string;
}

function ResourceSection<T>({
    sectionKey,
    type,
    title,
    rows,
    columns,
    toItem,
    selectable,
    selected,
    onToggle,
    emptyMessage,
}: ResourceSectionProps<T>) {
    const { t } = useTranslation('projects');
    const headingId = resourceSectionHeadingId(sectionKey);
    const items = rows.map(toItem);
    const keys = items.map((item) => resourceKey(item.type, item.id));
    const state = selectionState(keys, new Set(selected.keys()));

    const selectColumn: DataTableColumn<T> = {
        key: 'select',
        width: 56,
        label: (
            <TableCheckbox
                label={t('resources.selectAll', { section: title })}
                checked={state === 'all'}
                indeterminate={state === 'some'}
                disabled={rows.length === 0}
                onChange={() => onToggle(items, state !== 'all')}
            />
        ),
        render: (row) => {
            const item = toItem(row);
            return (
                <TableCheckbox
                    label={t('resources.selectOne', { name: item.name })}
                    checked={selected.has(resourceKey(type, item.id))}
                    onChange={(checked) => onToggle([item], checked)}
                />
            );
        },
    };

    return (
        <section aria-labelledby={headingId} className="w-full flex flex-col gap-3">
            <h2 id={headingId} tabIndex={-1} className="text-base font-bold">
                {t('resources.sectionTitle', { title, total: rows.length })}
            </h2>
            <div className="w-full rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                <DataTable
                    caption={title}
                    columns={selectable ? [selectColumn, ...columns] : columns}
                    rows={rows}
                    rowKey={(row) => toItem(row).id}
                    emptyMessage={emptyMessage}
                />
            </div>
        </section>
    );
}

interface ResourcesTabPanelProps {
    project: ProjectModel;
    /** Section whose heading should take focus once loaded (from the delete flow). */
    focusSection?: ResourceSectionKey;
    onSectionFocused?: () => void;
}

export default function ResourcesTabPanel({
    project,
    focusSection,
    onSectionFocused,
}: ResourcesTabPanelProps) {
    const { t } = useTranslation('projects');
    const { data, isPending, isError, refetch, isRefetching } = useProjectResourcesQuery(
        project.id
    );
    const [selected, setSelected] = useState<Selection>(new Map());
    const [moveOpen, setMoveOpen] = useState(false);
    const [hint, setHint] = useState('');
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const attachToolbar = useCallback((node: HTMLDivElement | null) => {
        toolbarRef.current = node;
        if (!node) return;
        return () => {
            toolbarRef.current = null;
            if (!node.contains(document.activeElement)) return;
            focusAfterRender(() =>
                document.querySelector<HTMLElement>(
                    '[role="tablist"] [role="tab"][aria-selected="true"]'
                )
            );
        };
    }, []);
    const movedTypeRef = useRef<MoveResourceType | null>(null);
    const openResource = useOpenInProject(project);

    useEffect(() => {
        if (!data || moveOpen) return;
        const present = new Set([
            ...(data.instances ?? []).map((row) => resourceKey('instance', row.id)),
            ...(data.baremetals ?? []).map((row) => resourceKey('baremetal', row.id)),
            ...(data.containers ?? []).map((row) => resourceKey('container', row.id)),
        ]);
        setSelected((previous) => new Map([...previous].filter(([key]) => present.has(key))));
    }, [data, moveOpen]);

    useEffect(() => {
        if (!data || !focusSection) return;
        document.getElementById(resourceSectionHeadingId(focusSection))?.focus();
        onSectionFocused?.();
    }, [data, focusSection, onSectionFocused]);

    const toggle = (items: MoveResourceItem[], select: boolean) => {
        setHint('');
        setSelected((previous) => {
            const next = new Map(previous);
            items.forEach((item) => {
                const key = resourceKey(item.type, item.id);
                if (select) next.set(key, item);
                else next.delete(key);
            });
            return next;
        });
    };

    const nameLink = (to: string, name: string) => (
        <Link
            to={to}
            onClick={openResource}
            className="min-h-6 min-w-6 inline-flex items-center font-medium underline-offset-2 hover:underline break-all"
        >
            {name}
        </Link>
    );

    const statusColumn = <T extends { status: string }>(): DataTableColumn<T> => ({
        key: 'status',
        label: t('resources.columns.status'),
        render: (row) => getStatusText(row.status, t),
    });

    const instanceColumns: DataTableColumn<ProjectInstanceSummary>[] = [
        {
            key: 'name',
            label: t('resources.columns.hostname'),
            render: (row) => nameLink(`/cloud-compute/${row.id}`, row.hostname),
        },
        statusColumn<ProjectInstanceSummary>(),
        {
            key: 'ip',
            label: t('resources.columns.publicIp'),
            render: (row) => row.ipAddress || '-',
        },
        {
            key: 'privateIp',
            label: t('resources.columns.privateIp'),
            render: (row) => row.privateIp || t('resources.none'),
        },
        {
            key: 'price',
            label: t('resources.columns.hourly'),
            align: 'right',
            render: (row) => t('resources.perHour', { price: formatBaht(row.priceHourly) }),
        },
    ];

    const baremetalColumns: DataTableColumn<ProjectBaremetalSummary>[] = [
        {
            key: 'name',
            label: t('resources.columns.hostname'),
            render: (row) => nameLink(`/bare-metal-compute/${row.id}`, row.hostname),
        },
        statusColumn<ProjectBaremetalSummary>(),
        {
            key: 'ip',
            label: t('resources.columns.publicIp'),
            render: (row) => row.ipAddress || '-',
        },
        {
            key: 'plan',
            label: t('resources.columns.plan'),
            render: (row) =>
                t('resources.perPeriod', {
                    price: formatBaht(row.pricePerPeriod),
                    period: t(`billing.periods.${row.paymentPeriod}`),
                }),
        },
        {
            key: 'nextCharge',
            label: t('resources.columns.nextCharge'),
            render: (row) => (row.nextCharge ? <DateTime iso={row.nextCharge} /> : '-'),
        },
    ];

    const containerColumns: DataTableColumn<ProjectContainerSummary>[] = [
        {
            key: 'name',
            label: t('resources.columns.name'),
            render: (row) => nameLink(`/container-engine/${row.id}`, row.name),
        },
        statusColumn<ProjectContainerSummary>(),
    ];

    const blockVolumeColumns: DataTableColumn<ProjectBlockVolumeSummary>[] = [
        {
            key: 'name',
            label: t('resources.columns.name'),
            render: (row) => <span className="font-medium break-all">{row.name}</span>,
        },
        {
            key: 'size',
            label: t('resources.columns.size'),
            render: (row) => t('resources.sizeGiB', { size: row.sizeGiB }),
        },
        {
            key: 'status',
            label: t('resources.columns.status'),
            render: (row) => <VolumeStatusBadge volume={row} />,
        },
        {
            key: 'attachedTo',
            label: t('resources.columns.attachedTo'),
            render: (row) => (
                <AttachmentSummary attachment={row.attachment} onNavigate={openResource} />
            ),
        },
    ];

    if (isPending) {
        return (
            <div aria-busy="true" className="w-full p-6 text-sm text-fg/78">
                <p role="status">{t('resources.loading')}</p>
            </div>
        );
    }

    if (isError || !data) return <LoadError onRetry={() => refetch()} />;

    const canSelect = (type: MoveResourceType) =>
        project.access[PRODUCT_OF_RESOURCE[type]] === 'manage';
    const noAccess = (
        [
            ['instances', 'vm'],
            ['baremetals', 'baremetal'],
            ['containers', 'container'],
            ['blockVolumes', 'block_storage'],
        ] as const
    )
        .filter(([key]) => !(key in data))
        .map(([, product]) => t(`products.${product}`));
    const anySelectable =
        (canSelect('instance') && (data.instances?.length ?? 0) > 0) ||
        (canSelect('baremetal') && (data.baremetals?.length ?? 0) > 0) ||
        (canSelect('container') && (data.containers?.length ?? 0) > 0);
    const count = selected.size;

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-1">
                <p className="max-w-[80ch] text-sm text-fg/78">{t('resources.description')}</p>
                {noAccess.length > 0 && (
                    <p className="max-w-[80ch] text-sm text-fg/78">
                        {t('resources.noAccess', { products: noAccess.join(', ') })}
                    </p>
                )}
            </div>

            {data.instances && (
                <ResourceSection
                    sectionKey="instances"
                    type="instance"
                    title={t('products.vm')}
                    rows={data.instances}
                    columns={instanceColumns}
                    toItem={(row) => ({ type: 'instance', id: String(row.id), name: row.hostname })}
                    selectable={canSelect('instance')}
                    selected={selected}
                    onToggle={toggle}
                    emptyMessage={t('resources.empty.instances')}
                />
            )}
            {data.baremetals && (
                <ResourceSection
                    sectionKey="baremetals"
                    type="baremetal"
                    title={t('products.baremetal')}
                    rows={data.baremetals}
                    columns={baremetalColumns}
                    toItem={(row) => ({
                        type: 'baremetal',
                        id: String(row.id),
                        name: row.hostname,
                    })}
                    selectable={canSelect('baremetal')}
                    selected={selected}
                    onToggle={toggle}
                    emptyMessage={t('resources.empty.baremetals')}
                />
            )}
            {data.containers === null && (
                <section
                    aria-labelledby={resourceSectionHeadingId('containers')}
                    className="w-full flex flex-col gap-3"
                >
                    <h2
                        id={resourceSectionHeadingId('containers')}
                        tabIndex={-1}
                        className="text-base font-bold"
                    >
                        {t('products.container')}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <p>{t('resources.containersUnavailable')}</p>
                        <Button
                            size="lg"
                            variant="secondary"
                            onClick={() => refetch()}
                            isLoading={isRefetching}
                        >
                            {t('common.retry')}
                        </Button>
                    </div>
                </section>
            )}
            {data.containers && (
                <ResourceSection
                    sectionKey="containers"
                    type="container"
                    title={t('products.container')}
                    rows={data.containers}
                    columns={containerColumns}
                    toItem={(row) => ({ type: 'container', id: row.id, name: row.name })}
                    selectable={canSelect('container')}
                    selected={selected}
                    onToggle={toggle}
                    emptyMessage={t('resources.empty.containers')}
                />
            )}

            {data.blockVolumes && (
                <section
                    aria-labelledby={resourceSectionHeadingId('blockVolumes')}
                    className="w-full flex flex-col gap-3"
                >
                    <h2
                        id={resourceSectionHeadingId('blockVolumes')}
                        tabIndex={-1}
                        className="text-base font-bold"
                    >
                        {t('resources.sectionTitle', {
                            title: t('products.block_storage'),
                            total: data.blockVolumes.length,
                        })}
                    </h2>
                    <p className="max-w-[80ch] text-sm text-fg/78">
                        {t('resources.blockVolumesNotMovable')}
                    </p>
                    <div className="w-full rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                        <DataTable
                            caption={t('products.block_storage')}
                            columns={blockVolumeColumns}
                            rows={data.blockVolumes}
                            rowKey={(row) => row.id}
                            emptyMessage={t('resources.empty.blockVolumes')}
                        />
                    </div>
                </section>
            )}

            <p role="status" className="sr-only">
                {hint || (count > 0 ? t('resources.selectedCount', { count }) : '')}
            </p>

            {anySelectable && (
                <div
                    ref={attachToolbar}
                    className="sticky bottom-0 z-10 w-full flex flex-wrap items-center justify-between gap-3 p-3 rounded-md border-[0.5px] border-line bg-surface-2 shadow-md"
                >
                    <span className="text-sm">{t('resources.selectedCount', { count })}</span>
                    <div className="flex flex-wrap gap-2">
                        {count > 0 && (
                            <Button
                                size="lg"
                                variant="secondary"
                                onClick={() => {
                                    setSelected(new Map());
                                    setHint(t('resources.selectionCleared'));
                                    toolbarRef.current
                                        ?.querySelector<HTMLButtonElement>('[data-move-selected]')
                                        ?.focus();
                                }}
                            >
                                {t('resources.clearSelection')}
                            </Button>
                        )}
                        <Button
                            size="lg"
                            data-move-selected
                            leftIcon={<IconArrowsExchange size={16} aria-hidden="true" />}
                            onClick={() => {
                                if (count === 0) {
                                    setHint(t('resources.selectFirst'));
                                    return;
                                }
                                setMoveOpen(true);
                            }}
                        >
                            {t('resources.moveSelected', { count })}
                        </Button>
                    </div>
                </div>
            )}

            <MoveResourcesModal
                opened={moveOpen}
                onClose={() => {
                    setMoveOpen(false);
                    const movedType = movedTypeRef.current;
                    movedTypeRef.current = null;
                    if (!movedType && toolbarRef.current) return;
                    focusAfterRender(() =>
                        movedType
                            ? document.getElementById(
                                  resourceSectionHeadingId(SECTION_OF_TYPE[movedType])
                              )
                            : document.querySelector<HTMLElement>(
                                  '[role="tablist"] [role="tab"][aria-selected="true"]'
                              )
                    );
                }}
                onMoved={(results) => {
                    movedTypeRef.current =
                        results.find((result) => result.status === 'moved')?.type ?? null;
                }}
                source={project}
                resources={[...selected.values()]}
            />
        </div>
    );
}
